import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAuditLog, type AuditLogFilters, type AuditLogRow } from "@/lib/api";
import {
  Button,
  Card,
  ErrorBanner,
  Field,
  PageContainer,
  TextInput,
  toErrorMessage,
} from "@/components/Forms";
import { RefreshIcon } from "@/components/Icons";

export default function AuditLogPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AuditLogFilters>({ limit: 100, offset: 0 });
  const [rows, setRows] = useState<AuditLogRow[] | null>(null);
  const [count, setCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [reloadAt, setReloadAt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    getAuditLog(filters)
      .then((res) => {
        if (cancelled) return;
        setRows(res.rows);
        setCount(res.count);
      })
      .catch((e) => !cancelled && setError(toErrorMessage(e, t("auditLog.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [filters, reloadAt, t]);

  function setField<K extends keyof AuditLogFilters>(key: K, value: AuditLogFilters[K]) {
    setFilters((f) => ({ ...f, [key]: value, offset: 0 }));
  }

  return (
    <PageContainer
      title={t("auditLog.title")}
      subtitle={t("auditLog.subtitle")}
      actions={
        <Button onClick={() => setReloadAt((n) => n + 1)}>
          <RefreshIcon size={14} /> {t("forms.refresh")}
        </Button>
      }
    >
      <ErrorBanner message={error} />

      <Card title={t("auditLog.apply")}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Field label={t("auditLog.filterTenant")}>
            <TextInput
              value={filters.tenant ?? ""}
              onChange={(e) => setField("tenant", e.target.value || undefined)}
              placeholder="alpha"
            />
          </Field>
          <Field label={t("auditLog.filterActor")}>
            <TextInput
              value={filters.actor ?? ""}
              onChange={(e) => setField("actor", e.target.value || undefined)}
              placeholder="ops@platform.local"
            />
          </Field>
          <Field label={t("auditLog.filterAction")}>
            <TextInput
              value={filters.action ?? ""}
              onChange={(e) => setField("action", e.target.value || undefined)}
              placeholder="lead.rearm"
            />
          </Field>
          <Field label={t("auditLog.filterSince")}>
            <TextInput
              value={filters.since ?? ""}
              onChange={(e) => setField("since", e.target.value || undefined)}
              placeholder="2026-05-01T00:00:00Z"
            />
          </Field>
        </div>
      </Card>

      <div className="h-3" />

      <Card title={`${t("auditLog.title")}${rows ? ` (${count})` : ""}`}>
        {rows === null ? (
          <p className="text-sm text-muted-foreground">{t("forms.loading")}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("auditLog.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-2 py-2">{t("auditLog.colAt")}</th>
                  <th className="text-left px-2 py-2">{t("auditLog.colActor")}</th>
                  <th className="text-left px-2 py-2">{t("auditLog.colAction")}</th>
                  <th className="text-left px-2 py-2">{t("auditLog.colTenant")}</th>
                  <th className="text-left px-2 py-2">{t("auditLog.colTarget")}</th>
                  <th className="text-left px-2 py-2">{t("auditLog.colDetail")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((row) => (
                  <tr key={row.id} className="align-top hover:bg-muted/30">
                    <td className="px-2 py-2 text-xs font-mono whitespace-nowrap">{row.createdAt}</td>
                    <td className="px-2 py-2 text-xs">{row.actor ?? "—"}</td>
                    <td className="px-2 py-2 font-mono text-xs">{row.action}</td>
                    <td className="px-2 py-2 font-mono text-xs">{row.tenant ?? "—"}</td>
                    <td className="px-2 py-2 text-xs">
                      {row.targetType ? (
                        <>
                          <span className="text-muted-foreground">{row.targetType}:</span>
                          <span className="font-mono">{row.targetId ?? "—"}</span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-xs max-w-md">
                      {row.details != null ? (
                        <code className="break-all">
                          {typeof row.details === "string"
                            ? row.details
                            : JSON.stringify(row.details)}
                        </code>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {rows && rows.length > 0 && (
          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {t("auditLog.page", {
                page: Math.floor((filters.offset ?? 0) / (filters.limit ?? 100)) + 1,
              })}{" "}
              · {rows.length}
            </span>
            <div className="flex gap-2">
              <Button
                onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, (f.offset ?? 0) - (f.limit ?? 100)) }))}
                disabled={(filters.offset ?? 0) === 0}
              >
                {t("auditLog.prev")}
              </Button>
              <Button
                onClick={() => setFilters((f) => ({ ...f, offset: (f.offset ?? 0) + (f.limit ?? 100) }))}
                disabled={rows.length < (filters.limit ?? 100)}
              >
                {t("auditLog.next")}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </PageContainer>
  );
}
